class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    // Delegated close-button handler — replaces former inline onclick handlers
    // in snippets/cart-drawer.liquid. Delegation on `this` survives the
    // innerHTML swap performed by renderContents() after add-to-cart.
    this.addEventListener('click', (evt) => {
      if (evt.target.closest('[data-close-cart]')) {
        evt.preventDefault();
        this.close();
      }
    });
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink)
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {this.classList.add('animate', 'active')});

    this.addEventListener('transitionend', () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    }, { once: true });

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if(cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    // FIX (wishlist add-to-cart bug): When the cart is empty on page load, the
    // cart-drawer snippet renders only the empty-state placeholder, and the
    // <cart-drawer> wrapper itself carries the `is-empty` class. Adding an
    // item via wishlist/quick-add calls renderContents() to swap in the
    // populated section HTML — but the previous code only stripped `is-empty`
    // from `.drawer__inner`, leaving it on the <cart-drawer> wrapper. CSS
    // (component-cart-drawer.css `cart-drawer.is-empty .drawer__inner`)
    // then kept the inner laid out as a single-row centered grid, hiding the
    // newly inserted line items even though the totals + cart-icon-bubble
    // updated correctly. Symptom: cart drawer opens, count/total update,
    // but the product row is invisible.
    //
    // Fix: toggle `is-empty` on BOTH the wrapper and the inner based on the
    // new server state, before re-rendering sections. renderContents is only
    // ever invoked after a successful /cart/add.js response, so by definition
    // the cart now contains at least one item — meaning we can confidently
    // strip `is-empty`. (We still compute it defensively in case a future
    // caller passes a full cart state object.)
    const innerEl = this.querySelector('.drawer__inner');
    let isEmptyAfterUpdate = false;
    if (typeof parsedState.item_count === 'number') {
      isEmptyAfterUpdate = parsedState.item_count === 0;
    } else if (Array.isArray(parsedState.items)) {
      isEmptyAfterUpdate = parsedState.items.length === 0;
    }
    // else: /cart/add.js single-item response — no item_count or items array,
    // but the add succeeded, so we know cart is non-empty (default false).

    this.classList.toggle('is-empty', isEmptyAfterUpdate);
    if (innerEl) innerEl.classList.toggle('is-empty', isEmptyAfterUpdate);

    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section => {
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      sectionElement.innerHTML =
          this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    }));

    // FIX: Removed duplicate overlay addEventListener here. The overlay click listener
    // is already bound in the constructor. Adding it again on every renderContents call
    // (i.e. every add-to-cart) stacked multiple handlers, causing close() to fire N+1
    // times per overlay click. The constructor binding covers all cases.
    setTimeout(() => {
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
